// Datos del cuestionario post-registro ("Account setup"). Copy e imágenes de los
// frames de Figma (paso 1: node 14:252; paso 2: node 105:360; paso 3: node 105:687).
// Las imágenes viven en `public/images/onboarding/{roles,categories,content}` y se
// referencian por URL absoluta, igual que el resto de assets del proyecto.

export interface SetupOption {
  /** Id estable (se persiste). */
  id: string;
  /** Etiqueta visible. */
  label: string;
  /** Imagen cuadrada. */
  image: string;
}

// ── Paso 1: rol (single-select) ──
export const SETUP_ROLES: SetupOption[] = [
  { id: 'community_manager', label: 'Community Manager', image: '/images/onboarding/roles/community.png' },
  { id: 'owner', label: 'Dueño/a', image: '/images/onboarding/roles/owner.png' },
  { id: 'employee', label: 'Empleado/a', image: '/images/onboarding/roles/employee.png' },
  { id: 'agency', label: 'Agencia', image: '/images/onboarding/roles/agency.png' },
];

// ── Paso 2: rubro / categoría de la marca ──
export const SETUP_CATEGORIES: SetupOption[] = [
  { id: 'moda', label: 'Moda e indumentaria', image: '/images/onboarding/categories/moda.png' },
  { id: 'hogar', label: 'Hogar y decoración', image: '/images/onboarding/categories/hogar.png' },
  { id: 'belleza', label: 'Belleza y cuidado personal', image: '/images/onboarding/categories/belleza.png' },
  { id: 'gastronomia', label: 'Gastronomía', image: '/images/onboarding/categories/gastronomia.png' },
  { id: 'salud', label: 'Salud y bienestar', image: '/images/onboarding/categories/salud.png' },
  { id: 'arte', label: 'Arte y manualidades', image: '/images/onboarding/categories/arte.png' },
  { id: 'contenido', label: 'Creación de contenido', image: '/images/onboarding/categories/contenido.png' },
  { id: 'tecnologia', label: 'Tecnología', image: '/images/onboarding/categories/tecnologia.png' },
  { id: 'libreria', label: 'Librería', image: '/images/onboarding/categories/libreria.png' },
  { id: 'jugueteria', label: 'Juguetería', image: '/images/onboarding/categories/jugueteria.png' },
  { id: 'automotriz', label: 'Automotriz', image: '/images/onboarding/categories/automotriz.png' },
  { id: 'otros', label: 'Otros', image: '/images/onboarding/categories/otros.png' },
];

/**
 * Id de categoría del paso 2 → **label de rubro** que espera el resto del sistema
 * (`rubroOptions` de `components/OnboardingSelects.tsx`, y con el que viven las ~1900
 * empresas existentes). El alta manda el label, no el id: `company_profiles.rubro` lo
 * leen el clasificador del backend, Brevo, el grupo de testing y varios modos.
 *
 * Ojo con el que falta: **"Muebles"** existe en `rubroOptions` pero NO tiene card en el
 * paso 2, así que ningún usuario nuevo puede quedar con ese rubro. Es una decisión
 * tomada, no un olvido — pero el modo Catálogo de muebles lo usa para decidir si extrae
 * medidas del título al importar (ADR-585), así que esos usuarios hay que corregirlos a
 * mano. Ver ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real.
 */
export const RUBRO_POR_CATEGORIA: Record<string, string> = {
  moda: 'Moda e Indumentaria',
  hogar: 'Hogar y Decoración',
  belleza: 'Belleza y Cuidado Personal',
  gastronomia: 'Gastronomía',
  salud: 'Salud y Bienestar',
  arte: 'Arte y Manualidades',
  contenido: 'Creación de Contenido',
  tecnologia: 'Tecnología',
  libreria: 'Librería',
  jugueteria: 'Juguetería',
  automotriz: 'Automotriz',
  otros: 'Otro',
};

/**
 * La vuelta de `RUBRO_POR_CATEGORIA`: el rubro de la marca ("Moda e Indumentaria") a su
 * categoría ("moda"), para los ejemplos por rubro del onboarding de marca. Un rubro que no
 * está en el mapa (escrito a mano, vacío) cae a `'otros'`.
 */
export function categoriaDelRubro(rubro: string): string {
  return Object.keys(RUBRO_POR_CATEGORIA).find((k) => RUBRO_POR_CATEGORIA[k] === rubro) ?? 'otros';
}

/**
 * Rubro a guardar a partir de lo elegido en el paso 2. Los roles multi-select
 * (community manager / agencia) pueden elegir varias categorías: se usa la **primera**,
 * que es la que el usuario tocó antes, porque `company_profiles.rubro` es un único
 * valor. El resto de las categorías no se pierde: el paso 2 las guarda en localStorage.
 */
export function rubroDesdeCategorias(categorias: string[]): string {
  const primera = categorias[0];
  return (primera && RUBRO_POR_CATEGORIA[primera]) || 'Otro';
}

/**
 * Ejemplos POSITIVOS de la modal "cómo subir una buena foto" (paso 4), **por rubro**.
 * Son los que ya usaba el onboarding clásico en `AddFirstProduct` y se recuperaron al
 * migrar: viven en el bucket `system` de Supabase Storage (no en `public/`, como el
 * resto de los assets del onboarding nuevo).
 *
 * Cada set son dos fotos: el **producto solo** y el **producto en uso / en contexto**,
 * que es justamente lo que la instrucción pide. Sólo hay tres sets —moda, hogar y uno
 * general— así que cualquier otro rubro cae al general (la botella), igual que antes.
 * Ver ADR-2026-08-01-instrucciones-de-foto-por-rubro.
 */
const STORAGE_SYSTEM =
  'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system';

export interface InstructionExamples {
  /** Producto solo. */
  solo: string;
  /** Producto siendo usado / en contexto. */
  enUso: string;
  /** Tip extra del rubro, si tiene. Se suma al texto general de la modal. */
  tip?: string;
  /**
   * Ejemplos de lo que hay que EVITAR. Sólo moda tiene: son las dos fotos que ya
   * estaban en `public/` (zapatillas movidas y un jean con fondo cargado), y son
   * claramente de indumentaria. Los rubros sin este set muestran el texto de "evitá…"
   * **sin fotos** — mejor eso que ilustrar con ropa a alguien que vende comida.
   */
  malas?: string[];
}

const EJEMPLOS_MODA: InstructionExamples = {
  solo: `${STORAGE_SYSTEM}/bolso.jpeg`,
  enUso: `${STORAGE_SYSTEM}/bolso_siendo_usado.png`,
  // El tip venía del onboarding clásico: era la única instrucción por rubro que había.
  tip: 'Las fotos del producto siendo utilizado (sin mostrar el rostro) funcionan mejor.',
  malas: [
    '/images/onboarding/product/instructions/bad-1.jpg',
    '/images/onboarding/product/instructions/bad-2.jpg',
  ],
};

const EJEMPLOS_HOGAR: InstructionExamples = {
  solo: `${STORAGE_SYSTEM}/sillon.png`,
  enUso: `${STORAGE_SYSTEM}/sillon_casa.png`,
};

const EJEMPLOS_GENERAL: InstructionExamples = {
  solo: `${STORAGE_SYSTEM}/botella.png`,
  enUso: `${STORAGE_SYSTEM}/botella_uso.png`,
};

/**
 * Ejemplos a mostrar según lo elegido en el paso 2. Con multi-select se usa la primera
 * categoría, igual que el rubro que se guarda (`rubroDesdeCategorias`), para que la
 * modal y la empresa no digan cosas distintas.
 */
export function getInstructionExamples(categorias: string[]): InstructionExamples {
  switch (categorias[0]) {
    case 'moda':
      return EJEMPLOS_MODA;
    case 'hogar':
      return EJEMPLOS_HOGAR;
    default:
      return EJEMPLOS_GENERAL;
  }
}

// ── Paso 3: tipo de contenido a crear (multi-select, todos los roles) ──
export const SETUP_CONTENT_TYPES: SetupOption[] = [
  { id: 'foto_producto', label: 'Foto catálogo', image: '/images/onboarding/content/foto-producto.png' },
  { id: 'videos', label: 'Videos', image: '/images/onboarding/content/videos.png' },
  { id: 'fotos_redes', label: 'Fotos para redes', image: '/images/onboarding/content/fotos-redes.png' },
];

// ── Paso 4: producto (subir foto propia o usar una imagen de ejemplo) ──
export interface ExampleProduct {
  /** Id estable (se persiste). */
  id: string;
  /** Nombre visible del producto de ejemplo. */
  name: string;
  /** Imagen cuadrada del producto. */
  image: string;
}

// Set por defecto (fallback y rubros que aún no tienen assets propios).
export const SETUP_EXAMPLE_PRODUCTS: ExampleProduct[] = [
  { id: 'cartera_verde', name: 'Cartera verde', image: '/images/onboarding/product/examples/cartera-verde.png' },
  { id: 'camisa_hombre', name: 'Camisa de hombre', image: '/images/onboarding/product/examples/camisa-hombre.png' },
];

// Set de **gastronomía** (primer rubro con resultados propios). Los assets viven en
// `public/images/onboarding/rubros/gastronomia/{hamburguesa,pizza}/` (ver README de esa
// carpeta). Los demás rubros (moda/belleza) se sumarán con el mismo patrón.
export const SETUP_EXAMPLE_PRODUCTS_GASTRONOMIA: ExampleProduct[] = [
  { id: 'gastro_hamburguesa', name: 'Hamburguesa', image: '/images/onboarding/rubros/gastronomia/hamburguesa/product.jpg' },
  { id: 'gastro_pizza', name: 'Pizza', image: '/images/onboarding/rubros/gastronomia/pizza/product.jpg' },
];

// Set de **hogar y decoración** (Figma node 192:1610). Assets en
// `public/images/onboarding/rubros/hogar/{mesa-luz,silla}/` (ver README de esa carpeta).
export const SETUP_EXAMPLE_PRODUCTS_HOGAR: ExampleProduct[] = [
  { id: 'hogar_mesa_luz', name: 'Mesa de luz', image: '/images/onboarding/rubros/hogar/mesa-luz/product.jpg' },
  { id: 'hogar_silla', name: 'Silla nórdica', image: '/images/onboarding/rubros/hogar/silla/product.jpg' },
];

// Set de **moda e indumentaria**. Assets en
// `public/images/onboarding/rubros/moda/{cartera-verde,camisa-hombre}/` (ver README).
export const SETUP_EXAMPLE_PRODUCTS_MODA: ExampleProduct[] = [
  { id: 'moda_cartera', name: 'Cartera verde', image: '/images/onboarding/rubros/moda/cartera-verde/product.jpg' },
  { id: 'moda_camisa', name: 'Camisa de hombre', image: '/images/onboarding/rubros/moda/camisa-hombre/product.jpg' },
];

// Set de **belleza y cuidado personal** (Figma node 192:1915). Assets en
// `public/images/onboarding/rubros/belleza/{mascara,lapiz}/` (ver README). La miniatura
// es el crop cuadrado de la foto **original** del producto, como en el mockup. ADR-797.
export const SETUP_EXAMPLE_PRODUCTS_BELLEZA: ExampleProduct[] = [
  { id: 'belleza_mascara', name: 'Máscara de pestañas', image: '/images/onboarding/rubros/belleza/mascara/product.jpg' },
  { id: 'belleza_lapiz', name: 'Lápiz labial', image: '/images/onboarding/rubros/belleza/lapiz/product.jpg' },
];

/**
 * Productos de ejemplo a mostrar en el **paso 4** según el/los rubro(s) elegidos en el
 * paso 2. Cada rubro con assets propios usa su set **sólo si es el ÚNICO rubro elegido**;
 * el resto cae al default. A medida que cada rubro tenga assets se agrega su caso.
 */
export function getExampleProducts(categories: string[]): ExampleProduct[] {
  if (categories.length === 1) {
    if (categories[0] === 'gastronomia') return SETUP_EXAMPLE_PRODUCTS_GASTRONOMIA;
    if (categories[0] === 'hogar') return SETUP_EXAMPLE_PRODUCTS_HOGAR;
    if (categories[0] === 'moda') return SETUP_EXAMPLE_PRODUCTS_MODA;
    if (categories[0] === 'belleza') return SETUP_EXAMPLE_PRODUCTS_BELLEZA;
  }
  return SETUP_EXAMPLE_PRODUCTS;
}

// ── Generación de imagen (pantalla de loading) ──
// El carrusel "Esto es lo que podés crear con Atlas" se mudó a
// `components/loading/loadingCarousel.ts` (`LOADING_CAROUSEL_IMAGES`) al pasar a usarse
// también en la app, no sólo en el alta. Ver ADR-2026-08-04-fotos-en-las-primeras-generaciones.

// ── Resultados de la generación (imágenes pre-cargadas por ejemplo) ──
export interface ResultImage {
  /**
   * Nombre del resultado (tipo de toma). Se muestra debajo de la miniatura **sólo en
   * los resultados de ejemplo** de este archivo, que son los que tienen un nombre que
   * dice algo; la generación real los numera ("Opción N") y ahí no se muestra
   * (ADR-2026-08-04-label-solo-en-los-resultados-de-ejemplo).
   *
   * Se usa siempre, se muestre o no: es la etiqueta accesible (`aria-label`, `alt`) y
   * el nombre del archivo al descargar.
   */
  label: string;
  /** Imagen optimizada (comprimida) para la grilla / preview rápido. */
  src: string;
  /**
   * Imagen ORIGINAL full-res para la vista ampliada. Si falta, se usa `src`.
   * Con la generación real: la URL del original sin comprimir que devuelve el
   * backend. La pantalla de resultados la **precarga por detrás**; la vista ampliada
   * muestra la optimizada (`src`) hasta que la original termina de cargar y recién
   * ahí hace el swap (ver `ResultImageOverlay`). Los ejemplos de demo tienen una
   * sola versión → `full` queda sin definir y todo cae a `src`.
   */
  full?: string;
  /**
   * Marca el resultado de tipo **"Video"** (Figma node 192:2220): en la grilla y el
   * preview se dibuja un **botón de play** sobre el póster (`src`). Si además hay `video`,
   * la vista ampliada lo **reproduce**; si no, muestra sólo el póster con el play.
   */
  isVideo?: boolean;
  /** MP4 opcional del resultado de video (se reproduce en el overlay ampliado). */
  video?: string;
}

/**
 * Resultados por producto de ejemplo preseleccionado en el paso 4. Para las fotos
 * subidas por el usuario la generación real aún no está implementada; el flujo cae
 * al set de `cartera_verde` como preview (ver ADR de la pantalla de resultados).
 */
export const GENERATION_RESULTS: Record<string, ResultImage[]> = {
  // ── Gastronomía ── (4 resultados por producto; el 3ro es "Video" con su MP4).
  //     Assets en `public/images/onboarding/rubros/gastronomia/{producto}/` (ver README).
  gastro_hamburguesa: [
    { label: 'Foto catálogo', src: '/images/onboarding/rubros/gastronomia/hamburguesa/catalogo.jpg' },
    { label: 'Foto para redes', src: '/images/onboarding/rubros/gastronomia/hamburguesa/redes.jpg' },
    {
      label: 'Video',
      src: '/images/onboarding/rubros/gastronomia/hamburguesa/video-poster.jpg',
      isVideo: true,
      video: '/images/onboarding/rubros/gastronomia/hamburguesa/video.mp4',
    },
    { label: 'Fondo blanco', src: '/images/onboarding/rubros/gastronomia/hamburguesa/fondo-blanco.jpg' },
  ],
  gastro_pizza: [
    { label: 'Foto catálogo', src: '/images/onboarding/rubros/gastronomia/pizza/catalogo.jpg' },
    { label: 'Foto para redes', src: '/images/onboarding/rubros/gastronomia/pizza/redes.jpg' },
    {
      label: 'Video',
      src: '/images/onboarding/rubros/gastronomia/pizza/video-poster.jpg',
      isVideo: true,
      video: '/images/onboarding/rubros/gastronomia/pizza/video.mp4',
    },
    { label: 'Fondo blanco', src: '/images/onboarding/rubros/gastronomia/pizza/fondo-blanco.jpg' },
  ],

  // ── Hogar y decoración ── (Figma node 192:1610; mismo formato que gastronomía).
  //     Assets en `public/images/onboarding/rubros/hogar/{producto}/` (ver README).
  hogar_mesa_luz: [
    { label: 'Foto catálogo', src: '/images/onboarding/rubros/hogar/mesa-luz/catalogo.jpg' },
    { label: 'Foto para redes', src: '/images/onboarding/rubros/hogar/mesa-luz/redes.jpg' },
    {
      label: 'Video',
      src: '/images/onboarding/rubros/hogar/mesa-luz/video-poster.jpg',
      isVideo: true,
      video: '/images/onboarding/rubros/hogar/mesa-luz/video.mp4',
    },
    { label: 'Fondo blanco', src: '/images/onboarding/rubros/hogar/mesa-luz/fondo-blanco.jpg' },
  ],
  hogar_silla: [
    { label: 'Foto catálogo', src: '/images/onboarding/rubros/hogar/silla/catalogo.jpg' },
    { label: 'Foto para redes', src: '/images/onboarding/rubros/hogar/silla/redes.jpg' },
    {
      label: 'Video',
      src: '/images/onboarding/rubros/hogar/silla/video-poster.jpg',
      isVideo: true,
      video: '/images/onboarding/rubros/hogar/silla/video.mp4',
    },
    { label: 'Fondo blanco', src: '/images/onboarding/rubros/hogar/silla/fondo-blanco.jpg' },
  ],

  // ── Belleza y cuidado personal ── (Figma node 192:1915; mismo formato y orden).
  //     Assets en `public/images/onboarding/rubros/belleza/{mascara,lapiz}/` (ver README).
  belleza_mascara: [
    { label: 'Foto catálogo', src: '/images/onboarding/rubros/belleza/mascara/catalogo.jpg' },
    { label: 'Foto para redes', src: '/images/onboarding/rubros/belleza/mascara/redes.jpg' },
    {
      label: 'Video',
      src: '/images/onboarding/rubros/belleza/mascara/video-poster.jpg',
      isVideo: true,
      video: '/images/onboarding/rubros/belleza/mascara/video.mp4',
    },
    { label: 'Fondo blanco', src: '/images/onboarding/rubros/belleza/mascara/fondo-blanco.jpg' },
  ],
  belleza_lapiz: [
    { label: 'Foto catálogo', src: '/images/onboarding/rubros/belleza/lapiz/catalogo.jpg' },
    { label: 'Foto para redes', src: '/images/onboarding/rubros/belleza/lapiz/redes.jpg' },
    {
      label: 'Video',
      src: '/images/onboarding/rubros/belleza/lapiz/video-poster.jpg',
      isVideo: true,
      video: '/images/onboarding/rubros/belleza/lapiz/video.mp4',
    },
    { label: 'Fondo blanco', src: '/images/onboarding/rubros/belleza/lapiz/fondo-blanco.jpg' },
  ],

  // ── Moda e indumentaria ── (mismo formato). Assets en `rubros/moda/{producto}/` (ver README).
  moda_cartera: [
    { label: 'Foto catálogo', src: '/images/onboarding/rubros/moda/cartera-verde/catalogo.jpg' },
    { label: 'Foto para redes', src: '/images/onboarding/rubros/moda/cartera-verde/redes.jpg' },
    {
      label: 'Video',
      src: '/images/onboarding/rubros/moda/cartera-verde/video-poster.jpg',
      isVideo: true,
      video: '/images/onboarding/rubros/moda/cartera-verde/video.mp4',
    },
    { label: 'Fondo blanco', src: '/images/onboarding/rubros/moda/cartera-verde/fondo-blanco.jpg' },
  ],
  moda_camisa: [
    { label: 'Foto catálogo', src: '/images/onboarding/rubros/moda/camisa-hombre/catalogo.jpg' },
    { label: 'Foto para redes', src: '/images/onboarding/rubros/moda/camisa-hombre/redes.jpg' },
    {
      label: 'Video',
      src: '/images/onboarding/rubros/moda/camisa-hombre/video-poster.jpg',
      isVideo: true,
      video: '/images/onboarding/rubros/moda/camisa-hombre/video.mp4',
    },
    { label: 'Fondo blanco', src: '/images/onboarding/rubros/moda/camisa-hombre/fondo-blanco.jpg' },
  ],

};

// ── Default (fallback) ── los rubros sin ejemplos propios ofrecen la misma cartera y la misma
// camisa que Moda, así que muestran sus resultados. Antes eran las fotos viejas (386×480)
// relabeladas: "Video" era una foto de estudio y "Fondo blanco" una de exterior
// (ADR-2026-10-31-resultados-del-ejemplo-por-defecto).
GENERATION_RESULTS.cartera_verde = GENERATION_RESULTS.moda_cartera;
GENERATION_RESULTS.camisa_hombre = GENERATION_RESULTS.moda_camisa;

/** Roles que en el paso 2 pueden elegir 1 o más categorías (multi-select). */
const MULTI_SELECT_ROLES = new Set(['community_manager', 'agency']);

/** ¿El rol permite multi-select en el paso 2? (community manager / agencia). */
export function isMultiSelectRole(role: string | null): boolean {
  return role != null && MULTI_SELECT_ROLES.has(role);
}

/** Cantidad total de pasos del cuestionario (para el progress bar "N de TOTAL"): nombre, rubro y
 *  producto (ADR-2026-10-31-onboarding-corto-para-la-prueba). */
export const SETUP_TOTAL_STEPS = 3;

/** Llaves de localStorage donde se guardan las respuestas hasta completar el alta. */
export const SETUP_ROLE_KEY = 'atlas:setup_role';
export const SETUP_CATEGORIES_KEY = 'atlas:setup_categories';
export const SETUP_CONTENT_KEY = 'atlas:setup_content';
/** El nombre de la marca del paso 1 del cuestionario corto. */
export const SETUP_BRAND_NAME_KEY = 'atlas:setup_brand_name';
/** Tope del nombre de la marca en el paso 1. */
export const SETUP_BRAND_NAME_MAX = 60;
/** Guarda qué se va a "generar": id del ejemplo (`cartera_verde`/`camisa_hombre`) o `upload`. */
export const SETUP_GENERATION_KEY = 'atlas:setup_generation';
/**
 * Ya no se escribe: decidía el upsell de planes al descargar, que se sacó
 * (ADR-2026-10-31-descargar-no-abre-los-planes). Queda sólo para que la limpieza borre el
 * valor viejo que hayan dejado los navegadores.
 */
export const SETUP_DOWNLOADED_KEY = 'atlas:setup_downloaded';

/**
 * Borra las respuestas del cuestionario. Se llama al **abandonar** el onboarding
 * (volver desde el paso 1, que desloguea): las llaves son globales del navegador, no
 * están namespaceadas por usuario, así que sin esto la próxima cuenta que se registre
 * en el mismo dispositivo arrancaría con las respuestas de la anterior a medio pisar.
 * Ver ADR-2026-08-03-volver-en-el-paso-1-desloguea.
 */
export function limpiarRespuestasDelCuestionario(): void {
  for (const key of [
    SETUP_ROLE_KEY,
    SETUP_CATEGORIES_KEY,
    SETUP_CONTENT_KEY,
    SETUP_GENERATION_KEY,
    SETUP_DOWNLOADED_KEY,
  ]) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* storage bloqueado (Safari privado): no hay nada que limpiar */
    }
  }
}
